import { makeStyles, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { findConditionalFormattingFieldName } from 'src/modules/conditional-formatting';
import { tr } from 'src/modules/i18n-helpers';

import { GlobalUnitMap } from '../modules/data';
import {
  ConditionalFormattingConditionTypes,
  ConditionalFormattingDetailDTO,
  ConditionalFormattingType,
} from '../modules/generated/api';
import { damageTypes, engineFuelDetails, equipments, getLabel, promotions, taxationTypes } from '../modules/labels';

import ConditionalFormattingColor from './ConditionalFormattingColor';
import UnitValue from './UnitValue';

type ConditionalFormattingColumnProps = {
  fieldCondition: ConditionalFormattingDetailDTO;
  field?: ConditionalFormattingType;
  fieldName?: string;
};

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
  },
  colorWrapper: {
    flexBasis: 0,
  },
}));

const f = (field?: ConditionalFormattingType, value?: number) => {
  if (!field) return value;
  const unit = GlobalUnitMap[field];
  return <UnitValue unit={GlobalUnitMap[field]} value={unit === '%' && value ? value / 100 : value} />;
};

const ConditionalFormattingColumnInfo = ({ fieldCondition, field, fieldName }: ConditionalFormattingColumnProps) => {
  const { backgroundColor, eq, gt, gte, lt, lte, contains, condition } = fieldCondition;
  const classes = useStyles();
  const { t } = useTranslation();

  let formatting;

  if (eq) {
    formatting = (
      <>
        {eq ? t('conditionalFormatting.equal').toLowerCase() : t('conditionalFormatting.contains').toLowerCase()}
        <em> {`"${eq || contains}"`}</em>
      </>
    );
  }

  const getFormattingLabel = (value: string, type?: ConditionalFormattingType) => {
    switch (type) {
      case ConditionalFormattingType.DamageType:
        return getLabel(damageTypes, value);
      case ConditionalFormattingType.Highlights:
        return getLabel(equipments, value);
      case ConditionalFormattingType.Promotions:
        return getLabel(promotions, value);
      case ConditionalFormattingType.NormalizedFuelDetail:
        return getLabel(engineFuelDetails, value);
      case ConditionalFormattingType.TaxType:
        return getLabel(taxationTypes, value);
      default:
        return contains;
    }
  };

  if (contains && !eq) {
    const containsValues = Array.isArray(contains) ? contains : [contains];
    const formattedContains = containsValues
      .map((value) => getFormattingLabel(value, field))
      .join(` ${t('common.or')} `);
    formatting = `${
      condition === ConditionalFormattingConditionTypes.ContainsNot
        ? t('conditionalFormatting.containsNot')
        : t('conditionalFormatting.contains')
    }: ${formattedContains}`;
  }

  if (gt !== undefined || gte !== undefined || lt !== undefined || lte !== undefined) {
    const hasLowerBound = gt || gte;
    const hasUpperBound = lt || lte;
    const hasLowerAndUpperBound = hasLowerBound && hasUpperBound;

    formatting =
      (hasLowerAndUpperBound && (
        <>
          {t('conditionalFormatting.gt_abbr')} {f(field, gt || gte)}
          <br />
          &amp; {t('conditionalFormatting.st_abbr').toLowerCase()} {f(field, lt || lte)}
        </>
      )) ||
      (hasLowerBound && (
        <>
          {t('conditionalFormatting.gt_abbr')} {f(field, gt || gte)}
        </>
      )) ||
      (hasUpperBound && (
        <>
          {t('conditionalFormatting.st_abbr')} {f(field, lt || lte)}
        </>
      ));
  }

  return formatting ? (
    <div className={classes.root}>
      <div className={classes.colorWrapper}>
        <ConditionalFormattingColor size={18} value={backgroundColor} />
      </div>
      {fieldName && (
        <Typography variant="caption" style={{ paddingRight: '2px' }}>
          {tr(`carsTableNext.filter.${findConditionalFormattingFieldName(fieldName)}`)}:
        </Typography>
      )}
      <Typography variant="caption">{formatting}</Typography>
    </div>
  ) : null;
};
export default ConditionalFormattingColumnInfo;
